import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { MonthRangeSelectorProps, MonthPickerHandle, DateRangeItemName } from "models/propTypes/datepickerPropTypes";
//#endregion
//#region components
import MessageBubble from "../messages/messageBubble";
import { Grid } from "@mui/material";
import MonthPicker from "components/common/datePickers/monthPicker";
//#endregion
import "react-datepicker/dist/react-datepicker.css";
import "theme/custom/react-datepicker.css";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const MonthRangeSelector: React.FC<MonthRangeSelectorProps> = (props) => {
  const startMonthPicker = React.useRef<MonthPickerHandle>(null);
  const endMonthPicker = React.useRef<MonthPickerHandle>(null);
  const [startExcludeMonths, setStartExcludeMonths] = React.useState<Date[]>([]);
  const [endExcludeMonths, setEndExcludeMonths] = React.useState<Date[]>([]);

  const onDateChange = React.useCallback(
    (
      name: DateRangeItemName,
      refName: DateRangeItemName,
      date: Date | undefined,
      refDate: Date | undefined,
      refPicker: React.RefObject<MonthPickerHandle>,
      setDateHandler: (date?: Date) => void,
      setExcludeMonthsHandlerRef: (dates: Date[]) => void
    ) => {
      setDateHandler(date);
      if (props.excludeRange) {
        if (date) {
          const yearMonth = DateHelper.getYearMonth(date);
          const excludeEndDates = props.excludeRange
            .filter((dr) => DateHelper.getYearMonth(dr[name]) === yearMonth)
            .map((d) => new Date(d[refName]));

          setExcludeMonthsHandlerRef(excludeEndDates);
        } else setExcludeMonthsHandlerRef([]);
      }
      setTimeout(() => {
        date && !refDate && refPicker.current && refPicker.current.setFocus();
      }, 5);
    },
    [props.excludeRange]
  );

  const onChange = (name: DateRangeItemName, date?: Date) => {
    switch (name) {
      case "start":
        onDateChange("start", "end", date, props.endDate, endMonthPicker, props.setStartDate, setEndExcludeMonths);
        break;
      case "end":
        onDateChange("end", "start", date, props.startDate, startMonthPicker, props.setEndDate, setStartExcludeMonths);
        break;
    }
  };

  return (
    <Grid
      container
      direction="row"
      spacing={1}
      className={clsx(props.className, cssExports.formFieldMessageAttachElemTop)}
    >
      <Grid item xs={6}>
        <MessageBubble message={props.startDateError} type={StyleEnums.DisplayType.Danger} placement="top" />
        <MonthPicker
          ref={startMonthPicker}
          selected={props.startDate}
          startDate={props.startDate}
          endDate={props.endDate}
          onChange={(date?: Date) => onChange("start", date)}
          error={props.error || !!props.startDateError}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.From)}
          minDate={props.minDate}
          maxDate={props.endDate}
          excludeDates={startExcludeMonths}
          displayType={props.displayType}
          onBlur={props.onStartBlur}
          onFocus={props.onStartFocus}
          success={props.success}
          hideClearable={props.hideClearable}
        />
      </Grid>
      <Grid item xs={6}>
        <MessageBubble message={props.endDateError} type={StyleEnums.DisplayType.Danger} placement="top" />
        <MonthPicker
          ref={endMonthPicker}
          selected={props.endDate}
          startDate={props.startDate}
          endDate={props.endDate}
          onChange={(date?: Date) => onChange("end", date)}
          error={props.error || !!props.endDateError}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.To)}
          minDate={props.startDate ?? props.minDate}
          excludeDates={endExcludeMonths}
          displayType={props.displayType}
          onBlur={props.onEndBlur}
          onFocus={props.onEndFocus}
          success={props.success}
          setLastDayOfMonth
          hideClearable={props.hideClearable}
        />
      </Grid>
    </Grid>
  );
};
export default MonthRangeSelector;
